import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Check, Clock, User, CheckCircle, ChevronLeft, ChevronRight, Download, Plus } from 'lucide-react';
import { DailyBrief, updateDocument, Agency, supabase } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';

export default function InfluencerBrief() {
  const params = useParams();

  // Support both URL formats:
  // 1. /brief/:agencyName/:influencerSlug (new format)
  // 2. /brief/:influencerSlug (old format for backwards compatibility)
  const influencerSlug = params.influencerSlug || params.agencyName; // fallback to agencyName if only one param
  const agencyNameFromUrl = params.agencyName && params.influencerSlug ? params.agencyName : null;

  const [allBriefs, setAllBriefs] = useState<DailyBrief[]>([]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [influencerName, setInfluencerName] = useState<string>('');
  const [agencyName, setAgencyName] = useState<string>(agencyNameFromUrl || 'Campaly');
  const [influencerProfileImage, setInfluencerProfileImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');

  useEffect(() => {
    loadBriefs();
  }, [influencerSlug]);

  const loadBriefs = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!influencerSlug) {
        setError('Lien invalide');
        return;
      }

      console.log('Searching for influencer slug:', influencerSlug);

      // Récupérer tous les briefs pour cet influenceur depuis Supabase
      const { data: briefsData, error: briefsError } = await supabase
        .from('daily_briefs')
        .select('*')
        .eq('influencer_slug', influencerSlug);

      if (briefsError) {
        console.error('Error loading briefs:', briefsError);
        setError('Erreur lors du chargement du brief');
        return;
      }

      console.log('Found briefs:', briefsData?.length || 0);

      if (!briefsData || briefsData.length === 0) {
        setError(`Aucun brief trouvé pour "${influencerSlug}". Vérifiez que le lien est correct.`);
        return;
      }

      const formattedBriefs = briefsData.map(brief => ({
        ...brief,
        date: typeof brief.date === 'string' ? new Date(brief.date) : brief.date
      })) as DailyBrief[];

      console.log('Briefs data:', formattedBriefs);

      setAllBriefs(formattedBriefs);
      if (formattedBriefs.length > 0) {
        const firstBrief = formattedBriefs[0];
        setInfluencerName(firstBrief.influencer_name || '');
        setAgencyName(firstBrief.agency_name || 'Campaly');

        // Load influencer profile image from Supabase using influencer_id
        if (firstBrief.influencer_id) {
          const { data: influencerData } = await supabase
            .from('influencers')
            .select('profile_image_url')
            .eq('id', firstBrief.influencer_id)
            .maybeSingle();

          if (influencerData) {
            setInfluencerProfileImage(influencerData.profile_image_url);
          }
        }
      }
      
      // Commencer par la date du jour
      const today = new Date();
      setCurrentDate(today);
      setSelectedDate(today);

    } catch (error) {
      console.error('Error loading briefs:', error);
      setError('Erreur lors du chargement du brief');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskToggle = async (briefId: string, taskId: string) => {
    try {
      const briefToUpdate = allBriefs.find(b => b.id === briefId);
      if (!briefToUpdate) return;

      const updatedTasks = briefToUpdate.tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            completed: !task.completed,
            completedAt: !task.completed ? new Date().toISOString() : null
          };
        }
        return task;
      });

      // Update in Supabase
      const { error: updateError } = await supabase
        .from('daily_briefs')
        .update({
          tasks: updatedTasks,
          updated_at: new Date().toISOString()
        })
        .eq('id', briefId);

      if (updateError) {
        console.error('Error updating task:', updateError);
        return;
      }

      // Update local state
      setAllBriefs(prev => prev.map(brief =>
        brief.id === briefId
          ? { ...brief, tasks: updatedTasks }
          : brief
      ));

    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return 'Date inconnue';

    if (typeof date === 'object' && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return '';

    if (typeof date === 'object' && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSameDay = (date1: Date | { seconds: number } | string, date2: Date) => {
    let d1: Date;
    if (typeof date1 === 'string') {
      d1 = new Date(date1);
    } else if (date1 && typeof date1 === 'object' && 'seconds' in date1) {
      d1 = new Date(date1.seconds * 1000);
    } else {
      d1 = date1 as Date;
    }

    // Normaliser les dates pour comparer uniquement jour/mois/année
    const d1Year = d1.getFullYear();
    const d1Month = d1.getMonth();
    const d1Day = d1.getDate();

    const d2Year = date2.getFullYear();
    const d2Month = date2.getMonth();
    const d2Day = date2.getDate();

    return d1Year === d2Year && d1Month === d2Month && d1Day === d2Day;
  };

  const getBriefsForDate = (date: Date) => {
    const briefs = allBriefs.filter(brief => isSameDay(brief.date, date));
    console.log(`🔍 getBriefsForDate for ${date.toISOString().split('T')[0]}:`, briefs.length, 'briefs found');
    console.log('🔍 All briefs in state:', allBriefs.map(b => ({ date: b.date, tasks: b.tasks.length })));
    return briefs;
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Start from Monday
    
    const days = [];
    const currentDay = new Date(startDate);
    
    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  const previousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleAddTask = async () => {
    if (!newTaskText.trim()) return;

    console.log('🔵 handleAddTask: Starting...');

    try {
      let selectedBriefs = getBriefsForDate(selectedDate);
      console.log('🔵 Selected briefs for date:', selectedBriefs.length);

      const newTask = {
        id: `task-${Date.now()}`,
        text: newTaskText.trim(),
        completed: false,
        added_by: 'creator' // Marqueur pour identifier que c'est le créateur qui l'a ajouté
      };

      // Si aucun brief n'existe pour cette date, on en crée un
      if (selectedBriefs.length === 0) {
        console.log('🔵 No brief found for this date, creating one...');

        // Récupérer un brief existant pour avoir les infos de base, ou utiliser les params de l'URL
        const referenceBrief = allBriefs.length > 0 ? allBriefs[0] : null;

        console.log('🔵 Reference brief found:', !!referenceBrief);
        console.log('🔵 URL params - influencerSlug:', influencerSlug, 'agencyName:', agencyName);

        if (!referenceBrief && (!influencerSlug || !agencyName)) {
          console.error('❌ No reference brief and no URL params');
          alert('Impossible de créer un brief. Aucune information trouvée.');
          return;
        }

        // Utiliser soit le brief de référence, soit les paramètres de l'URL
        const briefData = referenceBrief ? {
          agency_id: referenceBrief.agency_id,
          influencer_id: referenceBrief.influencer_id,
          influencer_name: referenceBrief.influencer_name,
          influencer_slug: referenceBrief.influencer_slug,
          agency_name: referenceBrief.agency_name
        } : {
          agency_id: '00000000-0000-0000-0000-000000000000', // ID temporaire si pas de référence
          influencer_id: influencerSlug || 'unknown',
          influencer_name: influencerSlug || 'unknown',
          influencer_slug: influencerSlug || 'unknown',
          agency_name: agencyName || 'unknown'
        };

        // Formater la date en YYYY-MM-DD sans conversion UTC
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        console.log('🔵 Creating brief for date:', dateString, 'from selectedDate:', selectedDate);

        // Créer un nouveau brief pour cette date
        const newBrief = {
          ...briefData,
          date: dateString,
          tasks: [newTask],
          internal_notes: ''
        };

        console.log('🔵 Creating brief with data:', newBrief);

        const { data, error } = await supabase
          .from('daily_briefs')
          .insert([newBrief])
          .select()
          .single();

        if (error) {
          console.error('❌ Error creating brief:', error);
          alert(`Erreur lors de la création du brief: ${error.message}`);
          return;
        }

        console.log('✅ Brief created successfully:', data);

        // Ajouter le nouveau brief à la liste
        const formattedBrief = {
          ...data,
          date: new Date(data.date)
        } as DailyBrief;

        setAllBriefs([...allBriefs, formattedBrief]);
        console.log('✅ Brief added to state');
      } else {
        console.log('🔵 Brief exists, adding task to it...');

        // On ajoute la tâche au premier brief de la journée
        const briefToUpdate = selectedBriefs[0];
        const updatedTasks = [...briefToUpdate.tasks, newTask];

        console.log('🔵 Updating brief with new tasks:', updatedTasks);

        const { error } = await supabase
          .from('daily_briefs')
          .update({
            tasks: updatedTasks,
            updated_at: new Date().toISOString()
          })
          .eq('id', briefToUpdate.id);

        if (error) {
          console.error('❌ Error adding task:', error);
          alert(`Erreur lors de l'ajout de la tâche: ${error.message}`);
          return;
        }

        console.log('✅ Task added successfully');

        // Mettre à jour l'état local
        setAllBriefs(allBriefs.map(brief =>
          brief.id === briefToUpdate.id
            ? { ...brief, tasks: updatedTasks }
            : brief
        ));
      }

      setNewTaskText('');
      setIsAddingTask(false);
      console.log('✅ handleAddTask: Complete!');
    } catch (error) {
      console.error('❌ Error adding task (catch):', error);
      alert(`Erreur lors de l'ajout de la tâche: ${error}`);
    }
  };

  const generateICSFile = (date: Date, tasks: Array<{ id: string; text: string; completed: boolean }>) => {
    // Format ICS date (YYYYMMDDTHHmmss)
    const formatICSDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const dateStr = formatICSDate(date);
    const tasksDescription = tasks.map((task, index) => `${index + 1}. ${task.text}`).join('\\n');

    // Create ICS content
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Campaly//Influencer Brief//FR
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:Tâches ${influencerName}
X-WR-TIMEZONE:Europe/Paris
BEGIN:VEVENT
DTSTART;VALUE=DATE:${dateStr}
DTEND;VALUE=DATE:${dateStr}
DTSTAMP:${formatICSDate(new Date())}T000000Z
UID:${dateStr}-${influencerSlug}@campaiyn.com
SUMMARY:Tâches ${agencyName} - ${tasks.length} tâche${tasks.length > 1 ? 's' : ''}
DESCRIPTION:${tasksDescription}
STATUS:CONFIRMED
SEQUENCE:0
BEGIN:VALARM
TRIGGER:-PT2H
ACTION:DISPLAY
DESCRIPTION:Rappel: Tâches à faire aujourd'hui
END:VALARM
END:VEVENT
END:VCALENDAR`;

    // Create blob and download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `taches-${formatDate(date).replace(/\s/g, '-')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const selectedBriefs = getBriefsForDate(selectedDate);
  const allTasks = selectedBriefs.flatMap(brief => brief.tasks);
  const completedTasks = allTasks.filter(task => task.completed).length;
  const totalTasks = allTasks.length;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  const calendarDays = getCalendarDays();
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center bg-[#1A1A1A] border border-[#2A2A2A] p-8 rounded-2xl max-w-md">
          <h1 className="text-xl font-semibold text-[#FAFAFA] mb-2">Brief non trouvé</h1>
          <p className="text-[#888888] text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.4s ease forwards; }
        .fade-up-1 { animation-delay: 0.05s; opacity: 0; }
        .fade-up-2 { animation-delay: 0.15s; opacity: 0; }
        .fade-up-3 { animation-delay: 0.25s; opacity: 0; }
        .task-row { transition: border-color 150ms ease, background 150ms ease; }
      `}</style>

      <div className="container mx-auto px-4 py-10 max-w-5xl">

        {/* Header */}
        <div className="fade-up fade-up-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 mb-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-[#D4AF37]/15 blur-md scale-110" />
                <InfluencerAvatar
                  profileImageUrl={influencerProfileImage}
                  name={influencerName}
                  size="lg"
                />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#FAFAFA]">Bonjour {influencerName} !</h1>
                <p className="text-[#888888] text-sm mt-0.5">Gérez vos tâches quotidiennes</p>
              </div>
            </div>
            <span className="text-[#888888] text-sm capitalize">{formatDate(selectedDate)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Calendrier */}
          <div className="fade-up fade-up-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#FAFAFA]">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <div className="flex items-center gap-1">
                <button onClick={previousMonth} className="p-2 rounded-lg hover:bg-[#2A2A2A] transition-colors">
                  <ChevronLeft className="w-4 h-4 text-[#888888]" />
                </button>
                <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-[#2A2A2A] transition-colors">
                  <ChevronRight className="w-4 h-4 text-[#888888]" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-xs font-medium text-[#555555] py-2 uppercase tracking-wider">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);
                const dayBriefs = getBriefsForDate(day);
                const hasTasks = dayBriefs.length > 0;
                const dayTasks = dayBriefs.flatMap(brief => brief.tasks);
                const completedDayTasks = dayTasks.filter(task => task.completed).length;
                const allDayTasksCompleted = dayTasks.length > 0 && completedDayTasks === dayTasks.length;

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(new Date(day))}
                    className={`
                      relative h-10 text-sm font-medium rounded-lg transition-all
                      ${!isCurrentMonth ? 'text-[#333333]' : 'text-[#AAAAAA]'}
                      ${isToday && !isSelected ? 'border border-[#D4AF37]/50 text-[#D4AF37]' : ''}
                      ${isSelected ? 'bg-[#D4AF37] text-[#0A0A0A] font-bold' : 'hover:bg-[#2A2A2A]'}
                      ${hasTasks && !isSelected && isCurrentMonth ? 'border border-[#3A3A3A]' : ''}
                    `}
                  >
                    {day.getDate()}
                    {hasTasks && (
                      <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                        allDayTasksCompleted ? 'bg-emerald-400' : 'bg-[#D4AF37]'
                      }`} />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 pt-4 border-t border-[#2A2A2A] flex items-center gap-5 text-xs text-[#888888]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                Tâches en cours
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                Tâches terminées
              </div>
            </div>
          </div>

          {/* Tâches du jour */}
          <div className="fade-up fade-up-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 flex flex-col">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-[#FAFAFA]">
                Tâches du {selectedDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
              </h2>
              <div className="flex items-center gap-3">
                {totalTasks > 0 && (
                  <>
                    <button
                      onClick={() => generateICSFile(selectedDate, allTasks)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-[#3A3A3A] text-[#AAAAAA] rounded-lg hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors text-xs font-medium"
                      title="Ajouter à l'agenda"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Agenda</span>
                    </button>
                    <span className={`text-sm font-medium ${progressPercentage === 100 ? 'text-emerald-400' : 'text-[#888888]'}`}>
                      {completedTasks}/{totalTasks}
                    </span>
                  </>
                )}
              </div>
            </div>

            {totalTasks > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#888888] uppercase tracking-wider">Progression</span>
                  <span className="text-xs font-semibold text-[#D4AF37]">{Math.round(progressPercentage)}%</span>
                </div>
                <div className="w-full bg-[#2A2A2A] rounded-full h-1.5">
                  <div
                    className="bg-[#D4AF37] h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <div className="flex items-center gap-4 text-xs text-[#555555] mt-3 pt-3 border-t border-[#2A2A2A]">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded border border-[#3A3A3A] bg-[#111111]" />
                    Tâches Swipe-upp
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded border border-[#D4AF37]/40 bg-[#D4AF37]/10" />
                    Mes tâches
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto space-y-2 pr-0.5">
              {selectedBriefs.length === 0 ? (
                <div className="text-center py-14">
                  <p className="text-[#888888] text-sm">Aucune tâche pour cette date.</p>
                </div>
              ) : (
                selectedBriefs.map((brief, briefIndex) => (
                  <div key={brief.id}>
                    {selectedBriefs.length > 1 && (
                      <p className="text-xs text-[#555555] uppercase tracking-wider mb-2">Brief {briefIndex + 1}</p>
                    )}
                    {brief.tasks.map((task) => {
                      const isCreatorTask = (task as any).added_by === 'creator';
                      return (
                        <div
                          key={task.id}
                          onClick={() => handleTaskToggle(brief.id!, task.id)}
                          className={`task-row flex items-start gap-3 p-4 rounded-xl border cursor-pointer ${
                            task.completed
                              ? 'bg-emerald-950/30 border-emerald-800/40'
                              : isCreatorTask
                              ? 'bg-[#D4AF37]/5 border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                              : 'bg-[#111111] border-[#2A2A2A] hover:border-[#3A3A3A]'
                          }`}
                        >
                          <div className={`mt-0.5 w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center transition-all ${
                            task.completed
                              ? 'bg-emerald-500 border-emerald-500'
                              : isCreatorTask
                              ? 'border-[#D4AF37]/50'
                              : 'border-[#3A3A3A]'
                          }`}>
                            {task.completed && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium leading-snug ${
                              task.completed ? 'line-through text-[#555555]' : isCreatorTask ? 'text-[#D4AF37]' : 'text-[#FAFAFA]'
                            }`}>
                              {task.text}
                            </p>
                            {task.completed && task.completedAt && (
                              <p className="text-xs text-emerald-600 mt-1">Terminé à {formatTime(task.completedAt)}</p>
                            )}
                            {isCreatorTask && !task.completed && (
                              <span className="inline-block text-[10px] px-1.5 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded mt-1">Ma tâche</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Ajout de tâche */}
            {(allBriefs.length > 0) && (
              <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
                {!isAddingTask ? (
                  <button
                    onClick={() => setIsAddingTask(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#FAFAFA] text-[#0A0A0A] rounded-xl text-sm font-semibold hover:bg-[#D4AF37] transition-colors duration-200"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter ma propre tâche
                  </button>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newTaskText}
                      onChange={(e) => setNewTaskText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddTask();
                        else if (e.key === 'Escape') { setIsAddingTask(false); setNewTaskText(''); }
                      }}
                      placeholder="Décrivez votre tâche..."
                      className="w-full px-4 py-2.5 bg-[#111111] border border-[#3A3A3A] text-[#FAFAFA] placeholder-[#555555] rounded-xl text-sm focus:outline-none focus:border-[#D4AF37]/60"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button onClick={handleAddTask} className="flex-1 py-2 bg-[#FAFAFA] text-[#0A0A0A] rounded-xl text-sm font-semibold hover:bg-[#D4AF37] transition-colors">
                        Ajouter
                      </button>
                      <button onClick={() => { setIsAddingTask(false); setNewTaskText(''); }} className="flex-1 py-2 bg-[#2A2A2A] text-[#888888] rounded-xl text-sm font-medium hover:bg-[#333333] transition-colors">
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-[#333333] text-xs">
            Powered by <span className="text-[#D4AF37] font-semibold">Campaly</span>
          </p>
        </div>
      </div>
    </div>
  );
}